import React, { useEffect, useState, useCallback, useMemo } from 'react';
import '../styles/formularios.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import ExcelJS from 'exceljs';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import logoISC from '../components/images//logo.png';
import logoSEP from '../components/images//SEP_Logo_2019.svg.png';
import firmaCoordinadora from '../components/images/firma_coordinadora.png';
import { resolveApiBaseList } from '../utils/apiBase';

const CATEGORY_COLOR_PALETTE = [
  { header: '#F4D03F', cell: '#FEF5E7' },
  { header: '#85C1E9', cell: '#EBF5FB' },
  { header: '#A9DFBF', cell: '#E9F7EF' },
  { header: '#F8BBD9', cell: '#FDEEEF' },
  { header: '#F9E79F', cell: '#FCF3CF' },
  { header: '#D2B4DE', cell: '#F5EEF8' }
];

const SPECIAL_COLUMN_COLORS = {
  periodo: { header: '#FAD7A0', cell: '#FCF3CF' },
  promedioGeneral: { header: '#D7BDE2', cell: '#F4ECF7' }
};

const CCT_CODE = '14PPR0155G';
const CCT_LABEL = `C.C.T. ${CCT_CODE}`;

const FASES_CONFIG = [
  {
    grados: [1, 2],
    clave: 'Fase 3',
    descripcionCorta: '1° y 2° de primaria',
    descripcion:
      'Fase 3 · Enfoque en estudiantes de 6 a 9 años para consolidar bases de lectura, escritura y pensamiento lógico a través de experiencias significativas.'
  },
  {
    grados: [3, 4],
    clave: 'Fase 4',
    descripcionCorta: '3° y 4° de primaria',
    descripcion:
      'Fase 4 · Dirigida a estudiantes de 9 a 11 años, fortalece el pensamiento crítico y la resolución de problemas en proyectos interdisciplinarios.'
  },
  {
    grados: [5, 6],
    clave: 'Fase 5',
    descripcionCorta: '5° y 6° de primaria',
    descripcion:
      'Fase 5 · Orientada a estudiantes de 11 a 13 años, integrando saberes para prepararlos a la siguiente etapa educativa con proyectos colaborativos.'
  }
];

const hexToExcelArgb = (hex = '#FFFFFF') => {
  const limpio = hex.replace('#', '').trim();
  const expandido = limpio.length === 3 ? limpio.split('').map((c) => c + c).join('') : limpio.padStart(6, '0');
  return `FF${expandido.toUpperCase()}`;
};

const hexToRgbArray = (hex = '#FFFFFF') => {
  const limpio = hex.replace('#', '').trim();
  const expandido = limpio.length === 3 ? limpio.split('').map((c) => c + c).join('') : limpio.padStart(6, '0');
  const valor = Number.parseInt(expandido, 16);
  const r = (valor >> 16) & 255;
  const g = (valor >> 8) & 255;
  const b = valor & 255;
  return [r, g, b];
};

const crearRelleno = (hex) => ({
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: hexToExcelArgb(hex) }
});

const BORDE_DELGADO = {
  top: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  left: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  bottom: { style: 'thin', color: { argb: 'FFBFBFBF' } },
  right: { style: 'thin', color: { argb: 'FFBFBFBF' } }
};

const limpiarBase64 = (dataUrl) => (typeof dataUrl === 'string' ? dataUrl.split(',')[1] ?? null : null);

const numeroAColumnaExcel = (numero = 1) => {
  let n = Math.max(1, Math.floor(numero));
  let columna = '';
  while (n > 0) {
    const resto = (n - 1) % 26;
    columna = String.fromCharCode(65 + resto) + columna;
    n = Math.floor((n - 1) / 26);
  }
  return columna;
};

const extraerGradoDesdeTexto = (valor) => {
  if (typeof valor !== 'string') return null;
  const coincidencia = valor.match(/\d+/);
  if (!coincidencia) return null;
  const numero = Number.parseInt(coincidencia[0], 10);
  return Number.isFinite(numero) ? numero : null;
};

const extraerGradoAlumno = (alumno = {}) => {
  const candidatos = [
    alumno.grado,
    alumno.Grado,
    alumno.gradoAsignado,
    alumno.GradoAsignado,
    alumno.gradoAlumno,
    alumno.gradoGrupo,
    alumno?.grupo
  ];

  for (const candidato of candidatos) {
    if (candidato === null || candidato === undefined) continue;
    if (typeof candidato === 'number' && Number.isFinite(candidato)) {
      return candidato;
    }
    const numero = extraerGradoDesdeTexto(String(candidato));
    if (numero !== null) {
      return numero;
    }
  }

  return null;
};

const determinarFasePorGrado = (grado) => {
  const numero = Number.parseInt(grado, 10);
  if (!Number.isFinite(numero)) return null;

  return (
    FASES_CONFIG.find(({ grados }) => grados.includes(numero)) ?? null
  );
};

const obtenerBase64DeImagen = async (recurso) => {
  if (!recurso) return null;
  try {
    const respuesta = await fetch(recurso);
    const blob = await respuesta.blob();
    return await new Promise((resolve, reject) => {
      const lector = new FileReader();
      lector.onload = () => resolve(lector.result);
      lector.onerror = () => reject(new Error('No fue posible procesar la imagen'));
      lector.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('❌ No fue posible cargar la imagen para el Excel:', recurso, error);
    return null;
  }
};

const ConsultarCalificaciones = () => {
  const [datosAlumno, setDatosAlumno] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sistema dual de almacenamiento
  const idUsuario = sessionStorage.getItem('idUsuario') || localStorage.getItem('idUsuario');
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantener mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) return {};

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }

        try {
          return JSON.parse(trimmed);
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Función para cargar las calificaciones del alumno
  const cargarCalificaciones = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🎓 Cargando calificaciones para usuario ID:', idUsuario);

      if (!idUsuario) {
        setError('No se encontró ID de usuario en la sesión');
        return;
      }

      const datos = await fetchJsonWithFallback(`/calificaciones/alumno/${encodeURIComponent(idUsuario)}`);
      if (!datos || typeof datos !== 'object') {
        throw new Error('Respuesta inválida del servidor');
      }
      console.log('📊 Datos de calificaciones recibidos:', datos);
      console.log('🔥 DATOS RAW:', JSON.stringify(datos, null, 2));
      
      // Debug detallado de la estructura
      console.log('🔍 Estructura detallada:');
      console.log('   👤 Alumno:', datos.alumno);
      console.log('   📚 Categorías:', Object.keys(datos.categorias || {}));
      console.log('   📅 Trimestres:', Object.keys(datos.trimestres || {}));
      console.log('   📊 Promedios:', datos.promedios);
      
      // Debug de cada categoría
      Object.entries(datos.categorias || {}).forEach(([catId, categoria]) => {
        console.log(`   📂 Categoría ${catId} (${categoria.nombre}):`, Object.keys(categoria.materias));
        Object.entries(categoria.materias).forEach(([matNombre, materia]) => {
          console.log(`     📖 ${matNombre}:`, Object.keys(materia.calificaciones || {}));
        });
      });
      
      setDatosAlumno(datos);

    } catch (error) {
      console.error('❌ Error al cargar calificaciones:', error);
      setError(`Error al cargar las calificaciones: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchJsonWithFallback, idUsuario]);

  // useEffect para cargar datos al montar el componente
  useEffect(() => {
    if (idUsuario) {
      cargarCalificaciones();
    }
  }, [idUsuario, cargarCalificaciones]);

  const categorias = useMemo(() => Object.values(datosAlumno?.categorias || {}), [datosAlumno]);
  const trimestres = useMemo(
    () => Object.keys(datosAlumno?.trimestres || {}).sort((a, b) => Number(a) - Number(b)),
    [datosAlumno]
  );
  const totalColumnasMaterias = useMemo(
    () => categorias.reduce((total, categoria) => total + Object.keys(categoria.materias || {}).length, 0),
    [categorias]
  );

  const gradoAlumno = useMemo(() => extraerGradoAlumno(datosAlumno?.alumno), [datosAlumno?.alumno]);
  const faseActual = useMemo(() => determinarFasePorGrado(gradoAlumno), [gradoAlumno]);
  const faseNumero = useMemo(() => {
    const match = faseActual?.clave?.match(/\d+/);
    return match ? match[0] : null;
  }, [faseActual]);

  const promediosCategorias = useMemo(() => {
    return categorias.map((categoria) => {
      let suma = 0;
      let contador = 0;

      Object.values(categoria.materias || {}).forEach((materia) => {
        Object.values(materia.calificaciones || {}).forEach((registro) => {
          const valor = parseFloat(registro?.calificacion);
          if (!Number.isNaN(valor)) {
            suma += valor;
            contador += 1;
          }
        });
      });

      return contador > 0 ? suma / contador : null;
    });
  }, [categorias]);

  const promediosCategoriaPorTrimestre = useMemo(() => {
    const resultado = {};

    trimestres.forEach((trimestre) => {
      resultado[trimestre] = categorias.map((categoria) => {
        let suma = 0;
        let contador = 0;

        Object.values(categoria.materias || {}).forEach((materia) => {
          const valor = parseFloat(materia.calificaciones?.[trimestre]?.calificacion);
          if (!Number.isNaN(valor)) {
            suma += valor;
            contador += 1;
          }
        });

        return contador > 0 ? suma / contador : null;
      });
    });

    return resultado;
  }, [categorias, trimestres]);

  const exportarExcel = useCallback(async () => {
    if (!datosAlumno) {
      alert('No se encontró información para exportar.');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Instituto Silviano Carrillo';
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet('Boleta', {
        properties: { defaultRowHeight: 22 },
        pageSetup: {
          orientation: 'landscape',
          paperSize: 9,
          fitToPage: true,
          fitToHeight: 0,
          fitToWidth: 1,
          margins: { left: 0.3, right: 0.3, top: 0.4, bottom: 0.4 }
        },
        views: [{ state: 'frozen', ySplit: 8 }]
      });

      const headerSuperior = [];
      const headerInferior = [];
      const columnasMeta = [];
      const categoriasInfo = categorias.map((categoria, index) => ({
        nombre: categoria.nombre,
        color: CATEGORY_COLOR_PALETTE[index % CATEGORY_COLOR_PALETTE.length],
        materiaCols: [],
        promedioCol: null
      }));

      const agregarColumna = (meta) => {
        columnasMeta.push(meta);
        headerSuperior.push(meta.headerSuperior ?? '');
        headerInferior.push(meta.headerInferior ?? '');
      };

      agregarColumna({
        tipo: 'periodo',
        headerSuperior: 'PERIODO',
        headerInferior: 'TRIMESTRE',
        colores: SPECIAL_COLUMN_COLORS.periodo,
        ancho: 18
      });

      categorias.forEach((categoria, catIndex) => {
        const materiasEntries = Object.entries(categoria.materias || {});
        const color = categoriasInfo[catIndex].color;

        materiasEntries.forEach(([nombreMateria], idxMateria) => {
          agregarColumna({
            tipo: 'materia',
            categoriaIndex: catIndex,
            categoriaRef: categoria,
            materiaNombre: nombreMateria,
            headerSuperior: idxMateria === 0 ? categoria.nombre.toUpperCase() : '',
            headerInferior: nombreMateria.toUpperCase(),
            colores: color
          });
        });

        if (materiasEntries.length > 0) {
          agregarColumna({
            tipo: 'promedioCategoria',
            categoriaIndex: catIndex,
            headerSuperior: '',
            headerInferior: 'PROMEDIO',
            colores: color,
            categoriaRef: categoria
          });
        }
      });

      agregarColumna({
        tipo: 'promedioGeneral',
        headerSuperior: 'PROMEDIO',
        headerInferior: 'GENERAL',
        colores: SPECIAL_COLUMN_COLORS.promedioGeneral,
        ancho: 18
      });

      const totalColumnas = columnasMeta.length;
      worksheet.columns = columnasMeta.map((meta) => ({
        width: meta.ancho ?? (meta.tipo === 'materia' ? 14 : 18)
      }));

      columnasMeta.forEach((meta, idx) => {
        const columna = idx + 1;
        if (meta.tipo === 'materia') {
          categoriasInfo[meta.categoriaIndex].materiaCols.push(columna);
        } else if (meta.tipo === 'promedioCategoria') {
          categoriasInfo[meta.categoriaIndex].promedioCol = columna;
        }
      });

      const tituloRow = worksheet.addRow([]);
      tituloRow.height = 36;
      worksheet.mergeCells(tituloRow.number, 1, tituloRow.number, totalColumnas);
      const tituloCell = tituloRow.getCell(1);
      tituloCell.value = 'INSTITUTO SILVIANO CARRILLO';
      tituloCell.font = { size: 20, bold: true, color: { argb: 'FF1F3B4D' } };
      tituloCell.alignment = { horizontal: 'center', vertical: 'middle' };

      const subtituloRow = worksheet.addRow([]);
      subtituloRow.height = 24;
      worksheet.mergeCells(subtituloRow.number, 1, subtituloRow.number, totalColumnas);
      const subtituloCell = subtituloRow.getCell(1);
      subtituloCell.value = 'Boleta de Evaluación';
      subtituloCell.font = { size: 14, bold: true };
      subtituloCell.alignment = { horizontal: 'center', vertical: 'middle' };

      const nombreRow = worksheet.addRow([]);
      nombreRow.height = 20;
      worksheet.mergeCells(nombreRow.number, 1, nombreRow.number, totalColumnas);
      const nombreCell = nombreRow.getCell(1);
      nombreCell.value = `Nombre del alumno: ${datosAlumno?.alumno?.nombre ?? '—'}`;
      nombreCell.font = { bold: true };
      nombreCell.alignment = { horizontal: 'left', vertical: 'middle' };

      const infoRow = worksheet.addRow([]);
      infoRow.height = 20;
      const segmento = Math.max(1, Math.floor(totalColumnas / 3));
      const rangosInfo = [
        { inicio: 1, fin: Math.min(segmento, totalColumnas), valor: `Grupo: ${datosAlumno?.alumno?.grupo ?? '—'}` },
        {
          inicio: Math.min(segmento + 1, totalColumnas),
          fin: Math.min(segmento * 2, totalColumnas),
          valor: `Ciclo escolar: ${datosAlumno?.alumno?.cicloEscolar ?? '2024-2025'}`
        },
        {
          inicio: Math.min(segmento * 2 + 1, totalColumnas),
          fin: totalColumnas,
          valor: `Fecha: ${new Date().toLocaleDateString('es-MX')}`
        }
      ].filter((rango) => rango.inicio <= rango.fin && rango.inicio <= totalColumnas);

      rangosInfo.forEach((rango) => {
        worksheet.mergeCells(infoRow.number, rango.inicio, infoRow.number, rango.fin);
        const celda = infoRow.getCell(rango.inicio);
        celda.value = rango.valor;
        celda.font = { bold: true };
        celda.alignment = { horizontal: 'left', vertical: 'middle' };
      });

      const faseRow = worksheet.addRow([]);
      faseRow.height = 18;
      const segmentoFase = Math.max(1, Math.floor(totalColumnas / 2));
      const faseLabel = `Fase: ${faseNumero ?? '—'}`;
      worksheet.mergeCells(faseRow.number, 1, faseRow.number, Math.min(segmentoFase, totalColumnas));
      const celdaFase = faseRow.getCell(1);
      celdaFase.value = faseLabel;
      celdaFase.font = { italic: true };
      celdaFase.alignment = { horizontal: 'left', vertical: 'middle' };

      const cctRow = worksheet.addRow([]);
      cctRow.height = 18;
      worksheet.mergeCells(cctRow.number, 1, cctRow.number, totalColumnas);
      const celdaCct = cctRow.getCell(1);
      celdaCct.value = CCT_LABEL;
      celdaCct.font = { italic: true };
      celdaCct.alignment = { horizontal: 'right', vertical: 'middle' };

      worksheet.addRow([]);

      const headerSuperiorRow = worksheet.addRow(headerSuperior);
      const headerInferiorRow = worksheet.addRow(headerInferior);
      headerSuperiorRow.height = 24;
      headerInferiorRow.height = 24;

      columnasMeta.forEach((meta, idx) => {
        const columna = idx + 1;
        const celdaSuperior = headerSuperiorRow.getCell(columna);
        const celdaInferior = headerInferiorRow.getCell(columna);
        const colorHeader = meta.colores?.header ?? '#FFFFFF';
        const colorBody = meta.colores?.cell ?? '#FFFFFF';

        celdaSuperior.fill = crearRelleno(colorHeader);
        celdaSuperior.font = { bold: true, color: { argb: 'FF000000' } };
        celdaSuperior.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        celdaSuperior.border = BORDE_DELGADO;

        celdaInferior.fill = crearRelleno(colorBody);
        celdaInferior.font = { bold: true, color: { argb: 'FF000000' } };
        celdaInferior.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        celdaInferior.border = BORDE_DELGADO;

        if (meta.tipo === 'periodo' || meta.tipo === 'promedioGeneral') {
          worksheet.mergeCells(headerSuperiorRow.number, columna, headerInferiorRow.number, columna);
          celdaInferior.value = null;
        }
      });

      categoriasInfo.forEach((info, idx) => {
        if (info.materiaCols.length === 0) return;
        const inicio = Math.min(...info.materiaCols);
        const fin = info.promedioCol ?? Math.max(...info.materiaCols);
        worksheet.mergeCells(headerSuperiorRow.number, inicio, headerSuperiorRow.number, fin);
        const celda = headerSuperiorRow.getCell(inicio);
        if (!celda.value) {
          celda.value = info.nombre.toUpperCase();
        }
        celda.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        celda.font = { bold: true, color: { argb: 'FF000000' } };
      });

      trimestres.forEach((trimestre) => {
        const valores = columnasMeta.map((meta) => {
          if (meta.tipo === 'periodo') {
            return `${trimestre}°`;
          }
          if (meta.tipo === 'materia') {
            const calificacion = meta.categoriaRef?.materias?.[meta.materiaNombre]?.calificaciones?.[trimestre]?.calificacion;
            return calificacion ?? '';
          }
          if (meta.tipo === 'promedioCategoria') {
            const valor = promediosCategoriaPorTrimestre[trimestre]?.[meta.categoriaIndex];
            if (valor === null || valor === undefined || Number.isNaN(valor)) return '';
            return Number.parseFloat(valor.toFixed(2));
          }
          if (meta.tipo === 'promedioGeneral') {
            const valor = datosAlumno?.promedios?.porTrimestre?.[trimestre];
            if (valor === null || valor === undefined || valor === '') return '';
            return Number(valor);
          }
          return '';
        });

        const filaDatos = worksheet.addRow(valores);
        filaDatos.height = 22;
        columnasMeta.forEach((meta, idx) => {
          const celda = filaDatos.getCell(idx + 1);
          if (meta.colores?.cell) {
            celda.fill = crearRelleno(meta.colores.cell);
          }
          celda.alignment = meta.tipo === 'periodo'
            ? { horizontal: 'center', vertical: 'middle' }
            : { horizontal: 'center', vertical: 'middle', wrapText: true };
          celda.font = { size: 11 };
          celda.border = BORDE_DELGADO;
        });

        const filaResumen = worksheet.addRow(columnasMeta.map(() => ''));
        filaResumen.height = 20;
        const celdaInicio = filaResumen.getCell(1);
        celdaInicio.value = `Prom. ${trimestre}°`;
        celdaInicio.font = { italic: true, bold: true, color: { argb: 'FF1F3B4D' } };
        celdaInicio.alignment = { horizontal: 'right', vertical: 'middle' };
        celdaInicio.fill = crearRelleno('#FDF2E9');
        celdaInicio.border = BORDE_DELGADO;

        categoriasInfo.forEach((info, catIdx) => {
          if (info.materiaCols.length === 0) return;
          const inicio = Math.min(...info.materiaCols);
          const fin = Math.max(...info.materiaCols);
          worksheet.mergeCells(filaResumen.number, inicio, filaResumen.number, fin);
          const celda = worksheet.getCell(filaResumen.number, inicio);
          const valor = promediosCategoriaPorTrimestre[trimestre]?.[catIdx];
          celda.value = valor !== null && valor !== undefined && !Number.isNaN(valor) ? Number(valor.toFixed(2)) : '-';
          celda.font = { italic: true, bold: true, color: { argb: 'FF1F3B4D' } };
          celda.alignment = { horizontal: 'center', vertical: 'middle' };
          celda.fill = crearRelleno(info.color.cell);
          celda.border = BORDE_DELGADO;

          for (let col = inicio + 1; col <= fin; col += 1) {
            filaResumen.getCell(col).border = BORDE_DELGADO;
          }

          const colProm = info.promedioCol;
          if (colProm) {
            filaResumen.getCell(colProm).border = BORDE_DELGADO;
          }
        });

        const celdaFinal = filaResumen.getCell(columnasMeta.length);
        celdaFinal.border = BORDE_DELGADO;
      });

      const filaCategoria = worksheet.addRow(columnasMeta.map(() => ''));
      filaCategoria.height = 22;
      const celdaTituloCategoria = filaCategoria.getCell(1);
      celdaTituloCategoria.value = 'PROM. CATEGORÍA';
      celdaTituloCategoria.font = { bold: true, color: { argb: 'FF1F3B4D' } };
      celdaTituloCategoria.alignment = { horizontal: 'right', vertical: 'middle' };
      celdaTituloCategoria.fill = crearRelleno('#E8DAEF');
      celdaTituloCategoria.border = BORDE_DELGADO;

      categoriasInfo.forEach((info, idx) => {
        if (info.materiaCols.length === 0) return;
        const inicio = Math.min(...info.materiaCols);
        const fin = Math.max(...info.materiaCols);
        worksheet.mergeCells(filaCategoria.number, inicio, filaCategoria.number, fin);
        const celda = worksheet.getCell(filaCategoria.number, inicio);
        const valor = promediosCategorias[idx];
        celda.value = valor !== null && valor !== undefined && !Number.isNaN(valor)
          ? Number(valor.toFixed(2))
          : '-';
        celda.font = { bold: true, color: { argb: 'FF1F3B4D' } };
        celda.alignment = { horizontal: 'center', vertical: 'middle' };
        celda.fill = crearRelleno(info.color.cell);
        celda.border = BORDE_DELGADO;

        for (let col = inicio + 1; col <= fin; col += 1) {
          filaCategoria.getCell(col).border = BORDE_DELGADO;
        }

        const colProm = info.promedioCol;
        if (colProm) {
          filaCategoria.getCell(colProm).border = BORDE_DELGADO;
        }
      });

      const filaPromedioGeneral = worksheet.addRow(columnasMeta.map(() => ''));
      filaPromedioGeneral.height = 24;
      const celdaTituloFinal = filaPromedioGeneral.getCell(1);
      celdaTituloFinal.value = 'PROMEDIO FINAL';
      celdaTituloFinal.font = { bold: true, color: { argb: 'FF1F3B4D' } };
      celdaTituloFinal.alignment = { horizontal: 'right', vertical: 'middle' };
      celdaTituloFinal.fill = crearRelleno('#F9EBEA');
      celdaTituloFinal.border = BORDE_DELGADO;

      if (columnasMeta.length > 2) {
        worksheet.mergeCells(filaPromedioGeneral.number, 2, filaPromedioGeneral.number, columnasMeta.length - 1);
        for (let col = 2; col <= columnasMeta.length - 1; col += 1) {
          filaPromedioGeneral.getCell(col).border = BORDE_DELGADO;
        }
      }

      const celdaPromedioGeneral = filaPromedioGeneral.getCell(columnasMeta.length);
      celdaPromedioGeneral.value = Number.parseFloat(datosAlumno?.promedios?.general ?? 0).toFixed(2);
      celdaPromedioGeneral.font = { bold: true, size: 13, color: { argb: 'FF1F3B4D' } };
      celdaPromedioGeneral.alignment = { horizontal: 'center', vertical: 'middle' };
      celdaPromedioGeneral.fill = crearRelleno(SPECIAL_COLUMN_COLORS.promedioGeneral.cell);
      celdaPromedioGeneral.border = BORDE_DELGADO;

      worksheet.addRow([]);

      const totalColumns = Math.max(totalColumnas, 1);
      const crearFilaVacia = () => new Array(totalColumns).fill('');
      const aplicarBordesFila = (row) => {
        for (let col = 1; col <= totalColumns; col += 1) {
          row.getCell(col).border = BORDE_DELGADO;
        }
      };

      const filaEncabezadoAsist = worksheet.addRow(crearFilaVacia());
      filaEncabezadoAsist.height = 20;
      filaEncabezadoAsist.getCell(1).value = 'ASISTENCIAS / INASISTENCIAS';
      filaEncabezadoAsist.getCell(1).font = { bold: true, color: { argb: 'FF1F3B4D' } };
      filaEncabezadoAsist.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
      filaEncabezadoAsist.getCell(1).fill = crearRelleno('#D5F5E3');
      filaEncabezadoAsist.getCell(1).border = BORDE_DELGADO;

      const inicioTrimestres = 2;
      const finTrimestres = Math.min(totalColumns - 1, inicioTrimestres + trimestres.length - 1);
      const columnasTrimestres = Math.max(finTrimestres - inicioTrimestres + 1, 0);
      const trimestresVisibles = trimestres.slice(0, columnasTrimestres);
      const inicioTotal = Math.min((finTrimestres >= inicioTrimestres ? finTrimestres + 1 : 2), totalColumns);

      trimestresVisibles.forEach((trimestre, idx) => {
        const celda = filaEncabezadoAsist.getCell(inicioTrimestres + idx);
        celda.value = `${trimestre}° T`;
        celda.font = { bold: true, color: { argb: 'FF1F3B4D' } };
        celda.alignment = { horizontal: 'center', vertical: 'middle' };
        celda.fill = crearRelleno('#D5F5E3');
        celda.border = BORDE_DELGADO;
      });

      if (inicioTotal <= totalColumns) {
        worksheet.mergeCells(filaEncabezadoAsist.number, inicioTotal, filaEncabezadoAsist.number, totalColumns);
      }
      const celdaTotalEncabezado = filaEncabezadoAsist.getCell(inicioTotal);
      celdaTotalEncabezado.value = 'TOTAL';
      celdaTotalEncabezado.font = { bold: true, color: { argb: 'FF1F3B4D' } };
      celdaTotalEncabezado.alignment = { horizontal: 'center', vertical: 'middle' };
      celdaTotalEncabezado.fill = crearRelleno('#D5F5E3');
      celdaTotalEncabezado.border = BORDE_DELGADO;
      aplicarBordesFila(filaEncabezadoAsist);

      const filaAsistencias = worksheet.addRow(crearFilaVacia());
      filaAsistencias.height = 20;
      const celdaAsistTitulo = filaAsistencias.getCell(1);
      celdaAsistTitulo.value = 'Asistencias';
      celdaAsistTitulo.font = { bold: true };
      celdaAsistTitulo.alignment = { horizontal: 'left', vertical: 'middle' };
      celdaAsistTitulo.fill = crearRelleno('#FCF3CF');
      celdaAsistTitulo.border = BORDE_DELGADO;

      trimestresVisibles.forEach((trimestre, idx) => {
        const celda = filaAsistencias.getCell(inicioTrimestres + idx);
        celda.value = datosAlumno?.resumenAsistencias?.porTrimestre?.[trimestre]?.asistencias ?? 0;
        celda.alignment = { horizontal: 'center', vertical: 'middle' };
        celda.fill = crearRelleno('#FEF9E7');
        celda.border = BORDE_DELGADO;
      });

      if (inicioTotal <= totalColumns) {
        worksheet.mergeCells(filaAsistencias.number, inicioTotal, filaAsistencias.number, totalColumns);
      }
      const celdaTotalAsist = filaAsistencias.getCell(inicioTotal);
      celdaTotalAsist.value = datosAlumno?.resumenAsistencias?.total?.asistencias ?? 0;
      celdaTotalAsist.font = { bold: true };
      celdaTotalAsist.alignment = { horizontal: 'center', vertical: 'middle' };
      celdaTotalAsist.fill = crearRelleno(SPECIAL_COLUMN_COLORS.promedioGeneral.cell);
      celdaTotalAsist.border = BORDE_DELGADO;
      aplicarBordesFila(filaAsistencias);

      const filaInasistencias = worksheet.addRow(crearFilaVacia());
      filaInasistencias.height = 20;
      const celdaInasTitulo = filaInasistencias.getCell(1);
      celdaInasTitulo.value = 'Inasistencias';
      celdaInasTitulo.font = { bold: true };
      celdaInasTitulo.alignment = { horizontal: 'left', vertical: 'middle' };
      celdaInasTitulo.fill = crearRelleno('#FDEDEC');
      celdaInasTitulo.border = BORDE_DELGADO;

      trimestresVisibles.forEach((trimestre, idx) => {
        const celda = filaInasistencias.getCell(inicioTrimestres + idx);
        celda.value = datosAlumno?.resumenAsistencias?.porTrimestre?.[trimestre]?.inasistencias ?? 0;
        celda.alignment = { horizontal: 'center', vertical: 'middle' };
        celda.fill = crearRelleno('#FDEDEC');
        celda.border = BORDE_DELGADO;
      });

      if (inicioTotal <= totalColumns) {
        worksheet.mergeCells(filaInasistencias.number, inicioTotal, filaInasistencias.number, totalColumns);
      }
      const celdaTotalInas = filaInasistencias.getCell(inicioTotal);
      celdaTotalInas.value = datosAlumno?.resumenAsistencias?.total?.inasistencias ?? 0;
      celdaTotalInas.font = { bold: true };
      celdaTotalInas.alignment = { horizontal: 'center', vertical: 'middle' };
      celdaTotalInas.fill = crearRelleno(SPECIAL_COLUMN_COLORS.promedioGeneral.cell);
      celdaTotalInas.border = BORDE_DELGADO;
      aplicarBordesFila(filaInasistencias);

      worksheet.addRow([]);

      const columnasDisponibles = Math.max(totalColumns - 1, 1);
      const anchoObservaciones = Math.max(1, Math.floor(columnasDisponibles / 2));
      const inicioObs = 2;
      const finObs = Math.min(totalColumns - 1, inicioObs + anchoObservaciones - 1);
      const inicioSug = Math.min(finObs + 1, totalColumns);

      const filaEncabezadoObs = worksheet.addRow(crearFilaVacia());
      filaEncabezadoObs.height = 22;
      const celdaPeriodoHead = filaEncabezadoObs.getCell(1);
      celdaPeriodoHead.value = 'PERIODO DE EVALUACIÓN';
      celdaPeriodoHead.font = { bold: true, color: { argb: 'FF1F3B4D' } };
      celdaPeriodoHead.alignment = { horizontal: 'center', vertical: 'middle' };
      celdaPeriodoHead.fill = crearRelleno('#D6EAF8');
      celdaPeriodoHead.border = BORDE_DELGADO;

      if (inicioObs <= finObs) {
        worksheet.mergeCells(filaEncabezadoObs.number, inicioObs, filaEncabezadoObs.number, finObs);
        const celdaObsHead = filaEncabezadoObs.getCell(inicioObs);
        celdaObsHead.value = 'OBSERVACIONES SOBRE LOS APRENDIZAJES';
        celdaObsHead.font = { bold: true, color: { argb: 'FF1F3B4D' } };
        celdaObsHead.alignment = { horizontal: 'center', vertical: 'middle' };
        celdaObsHead.fill = crearRelleno('#D6EAF8');
        celdaObsHead.border = BORDE_DELGADO;
      }

      if (inicioSug <= totalColumns) {
        worksheet.mergeCells(filaEncabezadoObs.number, inicioSug, filaEncabezadoObs.number, totalColumns);
        const celdaSugHead = filaEncabezadoObs.getCell(inicioSug);
        celdaSugHead.value = 'SUGERENCIAS POR TRIMESTRE';
        celdaSugHead.font = { bold: true, color: { argb: 'FF1F3B4D' } };
        celdaSugHead.alignment = { horizontal: 'center', vertical: 'middle' };
        celdaSugHead.fill = crearRelleno('#D6EAF8');
        celdaSugHead.border = BORDE_DELGADO;
      }
      aplicarBordesFila(filaEncabezadoObs);

      trimestres.forEach((trimestre) => {
        const filaObs = worksheet.addRow(crearFilaVacia());
        filaObs.height = 40;
        const celdaPeriodo = filaObs.getCell(1);
        celdaPeriodo.value = `${trimestre}°`;
        celdaPeriodo.alignment = { horizontal: 'center', vertical: 'middle' };
        celdaPeriodo.border = BORDE_DELGADO;

        if (inicioObs <= finObs) {
          worksheet.mergeCells(filaObs.number, inicioObs, filaObs.number, finObs);
          const celdaObs = filaObs.getCell(inicioObs);
          const textoObs = (datosAlumno?.observacionesGenerales?.[trimestre] || '').trim() || '—';
          celdaObs.value = textoObs;
          celdaObs.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
          celdaObs.border = BORDE_DELGADO;
        }

        if (inicioSug <= totalColumns) {
          worksheet.mergeCells(filaObs.number, inicioSug, filaObs.number, totalColumns);
          const celdaSug = filaObs.getCell(inicioSug);
          const textoSug = (datosAlumno?.sugerenciasGenerales?.[trimestre] || '').trim() || '—';
          celdaSug.value = textoSug;
          celdaSug.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
          celdaSug.border = BORDE_DELGADO;
        }
        aplicarBordesFila(filaObs);
      });

      worksheet.addRow([]);

      const filaFirma = worksheet.addRow(['Mtra. Ana María Ramos Cortés', 'Coordinadora de Primaria']);
      worksheet.mergeCells(filaFirma.number, 1, filaFirma.number, Math.ceil(totalColumnas / 2));
      worksheet.mergeCells(filaFirma.number, Math.ceil(totalColumnas / 2) + 1, filaFirma.number, totalColumnas);
      filaFirma.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
      filaFirma.getCell(Math.ceil(totalColumnas / 2) + 1).alignment = { horizontal: 'center', vertical: 'middle' };
      filaFirma.height = 30;
      filaFirma.eachCell((cell) => {
        cell.font = { bold: true };
      });

      const [logoIscBase64, logoSepBase64, firmaBase64] = await Promise.all([
        obtenerBase64DeImagen(logoISC),
        obtenerBase64DeImagen(logoSEP),
        obtenerBase64DeImagen(firmaCoordinadora)
      ]);

      if (logoIscBase64) {
        const logoIscId = workbook.addImage({ base64: limpiarBase64(logoIscBase64), extension: 'png' });
        worksheet.addImage(logoIscId, 'A1:C5');
      }

      if (logoSepBase64) {
        const logoSepId = workbook.addImage({ base64: limpiarBase64(logoSepBase64), extension: 'png' });
        const inicioDerecha = Math.max(totalColumnas - 2, 1);
        const finDerecha = Math.max(totalColumnas, inicioDerecha);
        const rangoDerecha = `${numeroAColumnaExcel(inicioDerecha)}1:${numeroAColumnaExcel(finDerecha)}5`;
        worksheet.addImage(logoSepId, rangoDerecha);
      }

      if (firmaBase64) {
        const firmaId = workbook.addImage({ base64: limpiarBase64(firmaBase64), extension: 'png' });
        const filaImagen = Math.max(filaFirma.number - 2, 1);
        const inicioFirma = Math.max(Math.floor(totalColumnas / 2) - 1, 1);
        const finFirma = Math.min(inicioFirma + 1, totalColumnas);
        const rangoFirma = `${numeroAColumnaExcel(inicioFirma)}${filaImagen}:${numeroAColumnaExcel(finFirma)}${filaImagen + 1}`;
        worksheet.addImage(firmaId, rangoFirma);
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      const nombreArchivo = datosAlumno?.alumno?.nombre
        ? `boleta_${datosAlumno.alumno.nombre.replace(/\s+/g, '_')}.xlsx`
        : 'boleta_alumno.xlsx';
      enlace.download = nombreArchivo;
      enlace.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
    } catch (error) {
      console.error('❌ Error al generar el Excel:', error);
      alert(`Error al generar el archivo Excel: ${error.message}`);
    }
  }, [categorias, datosAlumno, faseNumero, promediosCategoriaPorTrimestre, promediosCategorias, trimestres]);

  const exportarPDF = useCallback(async () => {
    if (!datosAlumno) {
      alert('No se encontró información para exportar.');
      return;
    }

    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });

      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 40;

      let finalY = 0;

      const [logoIscBase64, logoSepBase64, firmaBase64] = await Promise.all([
        obtenerBase64DeImagen(logoISC),
        obtenerBase64DeImagen(logoSEP),
        obtenerBase64DeImagen(firmaCoordinadora)
      ]);

      if (logoIscBase64) {
        doc.addImage(logoIscBase64, 'PNG', margin, 20, 90, 55);
      }
      if (logoSepBase64) {
        doc.addImage(logoSepBase64, 'PNG', pageWidth - margin - 90, 26, 90, 45);
      }

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Boleta de Evaluación', pageWidth / 2, 45, { align: 'center' });

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Nombre del Alumno: ${datosAlumno.alumno?.nombre || '—'}`, margin, 90);
      doc.text(`Grupo: ${datosAlumno.alumno?.grupo || '—'}`, margin + 260, 90);
      doc.text(`Ciclo Escolar: ${datosAlumno.alumno?.cicloEscolar || '2024-2025'}`, pageWidth - margin, 90, { align: 'right' });
      doc.text(CCT_LABEL, margin, 105);
      const faseLabel = `Fase: ${faseNumero ?? '—'}`;
      doc.text(faseLabel, pageWidth / 2, 105, { align: 'center' });
      doc.text(`Fecha: ${new Date().toLocaleDateString('es-MX')}`, pageWidth - margin, 105, { align: 'right' });

      let tablaStartY = 120;

      const materiasPorCategoria = categorias.map((categoria) => Object.keys(categoria.materias || {}).length);
      const materiaColumnInfo = [];
      categorias.forEach((categoria, catIndex) => {
        Object.keys(categoria.materias || {}).forEach((materia) => {
          materiaColumnInfo.push({ categoriaIndex: catIndex, materiaNombre: materia });
        });
      });

      const headRow1 = [
        {
          content: 'PERIODO DE EVALUACIÓN',
          rowSpan: 2,
          styles: {
            halign: 'center',
            valign: 'middle',
            fillColor: hexToRgbArray(SPECIAL_COLUMN_COLORS.periodo.header),
            textColor: hexToRgbArray('#1F3B4D'),
            fontStyle: 'bold'
          }
        }
      ];
      const headRow2 = [];

      const columnStyles = {
        0: {
          fillColor: hexToRgbArray(SPECIAL_COLUMN_COLORS.periodo.cell),
          textColor: hexToRgbArray('#1F3B4D'),
          halign: 'center',
          valign: 'middle'
        }
      };

      let currentColumnIndex = 1;
      categorias.forEach((categoria, catIndex) => {
        const materias = Object.keys(categoria.materias || {});
        if (materias.length === 0) {
          return;
        }

        const palette = CATEGORY_COLOR_PALETTE[catIndex % CATEGORY_COLOR_PALETTE.length];

        headRow1.push({
          content: categoria.nombre.toUpperCase(),
          colSpan: materias.length,
          styles: {
            halign: 'center',
            valign: 'middle',
            fillColor: hexToRgbArray(palette.header),
            textColor: hexToRgbArray('#1F3B4D'),
            fontStyle: 'bold'
          }
        });

        materias.forEach((nombreMateria) => {
          headRow2.push({
            content: nombreMateria.toUpperCase(),
            styles: {
              halign: 'center',
              valign: 'middle',
              fillColor: hexToRgbArray(palette.cell),
              textColor: hexToRgbArray('#1F3B4D'),
              fontStyle: 'bold'
            }
          });

          columnStyles[currentColumnIndex] = {
            halign: 'center',
            valign: 'middle'
          };

          currentColumnIndex += 1;
        });
      });

      headRow1.push({
        content: 'PROMEDIO',
        rowSpan: 2,
        styles: {
          halign: 'center',
          valign: 'middle',
          fillColor: hexToRgbArray(SPECIAL_COLUMN_COLORS.promedioGeneral.header),
          textColor: hexToRgbArray('#1F3B4D'),
          fontStyle: 'bold'
        }
      });

      const totalColumnasTabla = 1 + materiaColumnInfo.length + 1;
      columnStyles[totalColumnasTabla - 1] = {
        halign: 'center',
        valign: 'middle',
        fillColor: hexToRgbArray(SPECIAL_COLUMN_COLORS.promedioGeneral.cell),
        textColor: hexToRgbArray('#1F3B4D')
      };

      const head = [headRow1, headRow2];
      const body = [];

      trimestres.forEach((trimestre) => {
        const filaTrimestre = [`${trimestre}°`];
        materiaColumnInfo.forEach((info) => {
          const materiaData = categorias[info.categoriaIndex]?.materias?.[info.materiaNombre];
          const calificacion = materiaData?.calificaciones?.[trimestre]?.calificacion ?? '';
          filaTrimestre.push(calificacion === null || calificacion === undefined || calificacion === '' ? '-' : calificacion);
        });
        const promedioTrimestre = datosAlumno.promedios?.porTrimestre?.[trimestre];
        filaTrimestre.push(
          promedioTrimestre !== null && promedioTrimestre !== undefined && promedioTrimestre !== ''
            ? Number.parseFloat(promedioTrimestre).toFixed(2)
            : '-'
        );
        body.push(filaTrimestre);

        const filaResumen = [
          {
            content: `Prom. ${trimestre}°`,
            styles: {
              halign: 'right',
              fontStyle: 'bolditalic',
              fillColor: hexToRgbArray('#FDF2E9'),
              textColor: hexToRgbArray('#1F3B4D')
            }
          }
        ];

        categorias.forEach((categoria, catIndex) => {
          const materias = materiasPorCategoria[catIndex];
          const promedioCategoria = promediosCategoriaPorTrimestre[trimestre]?.[catIndex];
          for (let idx = 0; idx < materias; idx += 1) {
            if (idx === 0) {
              filaResumen.push({
                content:
                  promedioCategoria !== null && promedioCategoria !== undefined && !Number.isNaN(promedioCategoria)
                    ? Number.parseFloat(promedioCategoria).toFixed(2)
                    : '-',
                styles: {
                  halign: 'center',
                  fontStyle: 'bolditalic',
                  fillColor: hexToRgbArray('#FDEBD0'),
                  textColor: hexToRgbArray('#1F3B4D')
                }
              });
            } else {
              filaResumen.push({
                content: '',
                styles: {
                  fillColor: hexToRgbArray('#FDEBD0')
                }
              });
            }
          }
        });

        filaResumen.push({ content: '', styles: { fillColor: hexToRgbArray('#FDEBD0') } });
        body.push(filaResumen);
      });

      const filaPromedioCategoria = [
        {
          content: 'PROM. CATEGORÍA',
          styles: {
            halign: 'right',
            fontStyle: 'bold',
            fillColor: hexToRgbArray('#E8DAEF'),
            textColor: hexToRgbArray('#1F3B4D')
          }
        }
      ];

      categorias.forEach((categoria, catIndex) => {
        const materias = materiasPorCategoria[catIndex];
        const promedioCategoria = promediosCategorias[catIndex];
        for (let idx = 0; idx < materias; idx += 1) {
          if (idx === 0) {
            filaPromedioCategoria.push({
              content:
                promedioCategoria !== null && promedioCategoria !== undefined && !Number.isNaN(promedioCategoria)
                  ? Number.parseFloat(promedioCategoria).toFixed(2)
                  : '-',
              styles: {
                halign: 'center',
                fontStyle: 'bold',
                fillColor: hexToRgbArray('#E8DAEF'),
                textColor: hexToRgbArray('#1F3B4D')
              }
            });
          } else {
            filaPromedioCategoria.push({ content: '', styles: { fillColor: hexToRgbArray('#E8DAEF') } });
          }
        }
      });
      filaPromedioCategoria.push({ content: '', styles: { fillColor: hexToRgbArray('#E8DAEF') } });
      body.push(filaPromedioCategoria);

      const filaPromedioFinal = [
        {
          content: 'PROMEDIO FINAL',
          styles: {
            halign: 'right',
            fontStyle: 'bold',
            fillColor: hexToRgbArray('#F9EBEA'),
            textColor: hexToRgbArray('#1F3B4D')
          }
        }
      ];
      for (let idx = 0; idx < materiaColumnInfo.length; idx += 1) {
        filaPromedioFinal.push({ content: '', styles: { fillColor: hexToRgbArray('#F9EBEA') } });
      }
      filaPromedioFinal.push({
        content: Number.parseFloat(datosAlumno?.promedios?.general ?? 0).toFixed(2),
        styles: {
          halign: 'center',
          fontStyle: 'bold',
          fillColor: hexToRgbArray(SPECIAL_COLUMN_COLORS.promedioGeneral.cell),
          textColor: hexToRgbArray('#1F3B4D')
        }
      });
      body.push(filaPromedioFinal);

      doc.autoTable({
        startY: tablaStartY,
        head,
        body,
        styles: {
          fontSize: 8.5,
          halign: 'center',
          cellPadding: 4
        },
        headStyles: {
          fontStyle: 'bold',
          textColor: hexToRgbArray('#1F3B4D'),
          lineWidth: 0.2,
          lineColor: [150, 150, 150]
        },
        columnStyles,
        theme: 'grid'
      });

      finalY = doc.lastAutoTable.finalY;

      const asistenciaHead = [[
        'ASISTENCIAS / INASISTENCIAS',
        ...trimestres.map((t) => `${t}° T`),
        'TOTAL'
      ]];

      const asistRow = [
        { content: 'Asistencias', styles: { fontStyle: 'bold', fillColor: hexToRgbArray('#FCF3CF'), textColor: hexToRgbArray('#1F3B4D') } },
        ...trimestres.map((t) => datosAlumno?.resumenAsistencias?.porTrimestre?.[t]?.asistencias ?? 0),
        datosAlumno?.resumenAsistencias?.total?.asistencias ?? 0
      ];

      const inasRow = [
        { content: 'Inasistencias', styles: { fontStyle: 'bold', fillColor: hexToRgbArray('#FDEDEC'), textColor: hexToRgbArray('#1F3B4D') } },
        ...trimestres.map((t) => datosAlumno?.resumenAsistencias?.porTrimestre?.[t]?.inasistencias ?? 0),
        datosAlumno?.resumenAsistencias?.total?.inasistencias ?? 0
      ];

      doc.autoTable({
        startY: finalY + 20,
        head: asistenciaHead,
        body: [asistRow, inasRow],
        styles: {
          fontSize: 9,
          halign: 'center',
          cellPadding: 4
        },
        headStyles: {
          fillColor: hexToRgbArray('#D5F5E3'),
          textColor: hexToRgbArray('#1F3B4D'),
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'left', fontStyle: 'bold' }
        },
        theme: 'grid'
      });

      finalY = doc.lastAutoTable.finalY;

      const tablaObservaciones = trimestres.map((trimestre) => ([
        `${trimestre}°`,
        (datosAlumno?.observacionesGenerales?.[trimestre] || '').trim() || '—',
        (datosAlumno?.sugerenciasGenerales?.[trimestre] || '').trim() || '—'
      ]));

      doc.autoTable({
        startY: finalY + 25,
        head: [[
          'PERIODO DE EVALUACIÓN',
          'OBSERVACIONES SOBRE LOS APRENDIZAJES',
          'SUGERENCIAS POR TRIMESTRE'
        ]],
        body: tablaObservaciones,
        styles: {
          fontSize: 9,
          cellPadding: 6,
          halign: 'left'
        },
        headStyles: {
          fillColor: hexToRgbArray('#D6EAF8'),
          textColor: hexToRgbArray('#1F3B4D'),
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 70 },
          1: { cellWidth: (pageWidth - margin * 2 - 70) / 2, halign: 'left' },
          2: { cellWidth: (pageWidth - margin * 2 - 70) / 2, halign: 'left' }
        },
        theme: 'grid'
      });

      finalY = doc.lastAutoTable.finalY;

      if (finalY + 100 > pageHeight) {
        doc.addPage();
        finalY = margin;
      } else {
        finalY += 40;
      }

      if (firmaBase64) {
        doc.addImage(firmaBase64, 'PNG', pageWidth / 2 - 60, finalY, 120, 45);
      }

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Mtra. Ana María Ramos Cortés', pageWidth / 2, finalY + 60, { align: 'center' });
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Coordinadora de Primaria', pageWidth / 2, finalY + 75, { align: 'center' });

      const nombreArchivo = datosAlumno?.alumno?.nombre
        ? `boleta_${datosAlumno.alumno.nombre.replace(/\s+/g, '_')}.pdf`
        : 'boleta_alumno.pdf';
      doc.save(nombreArchivo);
    } catch (error) {
      console.error('❌ Error al generar PDF:', error);
      alert('Error al generar el PDF: ' + error.message);
    }
  }, [categorias, datosAlumno, faseNumero, promediosCategoriaPorTrimestre, promediosCategorias, trimestres]);


  useEffect(() => {
    window.exportarExcel = exportarExcel;
    window.exportarPDF = exportarPDF;

    return () => {
      delete window.exportarExcel;
      delete window.exportarPDF;
    };
  }, [exportarExcel, exportarPDF]);

  // Mostrar loading
  if (loading) {
    return (
      <div className="bg-light py-4">
        <div className="container text-center">
          <h2 className="mb-4">Consulta de Calificaciones</h2>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando calificaciones...</p>
        </div>
      </div>
    );
  }

  // Mostrar error
  if (error) {
    return (
      <div className="bg-light py-4">
        <div className="container text-center">
          <h2 className="mb-4">Consulta de Calificaciones</h2>
          <div className="alert alert-danger" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
          <button className="btn btn-primary" onClick={cargarCalificaciones}>
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Mostrar mensaje si no hay datos
  if (!datosAlumno) {
    return (
      <div className="bg-light py-4">
        <div className="container text-center">
          <h2 className="mb-4">Consulta de Calificaciones</h2>
          <div className="alert alert-info" role="alert">
            <i className="fas fa-info-circle me-2"></i>
            No se encontraron calificaciones para este alumno.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light py-4">
      <div className="container">
        <h2 className="text-center mb-4">Consulta de Calificaciones</h2>

        <div id="boleta" className="boleta" style={{ fontSize: '12px', maxWidth: '100%', overflow: 'hidden' }}>
          <div className="d-flex justify-content-between align-items-center mb-2 px-2">
            <img src={logoISC} alt="Logo ISC" style={{ height: '60px', objectFit: 'contain' }} />
            <div className="text-end">
              <img src={logoSEP} alt="Logo SEP" style={{ height: '40px', objectFit: 'contain' }} />
            </div>
          </div>

          <div className="text-center fw-bold text-black mb-2" style={{ fontSize: '18px' }}>Boleta de Evaluación</div>

          <div className="row mb-2" style={{ fontSize: '11px' }}>
            <div className="col"><strong>Nombre(s) del Alumno:</strong> <span id="nombreAlumno">{datosAlumno?.alumno?.nombre || 'Cargando...'}</span></div>
            <div className="col"><strong>Grupo:</strong> <span id="grupoAlumno">{datosAlumno?.alumno?.grupo || 'Cargando...'}</span></div>
            <div className="col"><strong>Ciclo:</strong> {datosAlumno?.alumno?.cicloEscolar || '2024-2025'}</div>
            <div className="col text-end"><strong>Fecha:</strong> {new Date().toLocaleDateString('es-ES')}</div>
          </div>

          <div className="row mb-2" style={{ fontSize: '11px' }}>
            <div className="col"><strong>C.C.T.:</strong> {CCT_CODE}</div>
            <div className="col"><strong>Fase:</strong> {faseNumero != null ? faseNumero : 'Por definir'}</div>
          </div>

          <table id="tablaBoleta" className="table table-bordered table-sm text-center" style={{ fontSize: '9px', marginBottom: '10px' }}>
            <thead>
              {/* Fila 1: PERIODO + Categorías dinámicas */}
              <tr>
                <th rowSpan="2">PERIODO TRIMESTRE</th>
                {categorias.map((categoria, index) => {
                  const numMaterias = Object.keys(categoria.materias || {}).length;
                  return numMaterias > 0 ? (
                    <th key={index} colSpan={numMaterias} 
                        style={{
                          backgroundColor: index === 0 ? '#F4D03F' : 
                                         index === 1 ? '#85C1E9' : 
                                         index === 2 ? '#A9DFBF' : '#F8BBD9',
                          color: '#000'
                        }}>
                      {categoria.nombre}
                    </th>
                  ) : null;
                })}
                <th rowSpan="2">PROMEDIO</th>
              </tr>
              {/* Fila 2: Nombres de materias dinámicas */}
              <tr>
                {categorias.map((categoria) => 
                  Object.keys(categoria.materias || {}).map((nombreMateria, materiaIndex) => (
                    <th key={`${categoria.nombre}-${materiaIndex}`} className="rotated">
                      <div>{nombreMateria.toUpperCase()}</div>
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody id="tablaAlumno">
              {trimestres.map(trimestre => {
                console.log(`🔍 Procesando trimestre ${trimestre} dinámicamente`);
                
                return (
                  <React.Fragment key={trimestre}>
                    <tr>
                      <td><strong>{trimestre}°</strong></td>
                      {/* Generar celdas dinámicamente basado en las categorías y materias reales */}
                      {categorias.map((categoria) => 
                        Object.entries(categoria.materias || {}).map(([nombreMateria, materiaData], materiaIndex) => (
                          <td key={`${trimestre}-${categoria.nombre}-${materiaIndex}`}>
                            {materiaData.calificaciones?.[trimestre]?.calificacion || '-'}
                          </td>
                        ))
                      )}
                      {/* Promedio del trimestre */}
                      <td><strong>{datosAlumno.promedios?.porTrimestre?.[trimestre] || '-'}</strong></td>
                    </tr>
                    <tr className="table-primary">
                      <td className="text-end text-primary fw-bold" style={{ fontSize: '0.7rem' }}>
                        Prom. {trimestre}°
                      </td>
                      {categorias.map((categoria, catIndex) => {
                        const numMaterias = Object.keys(categoria.materias || {}).length;
                        if (numMaterias === 0) {
                          return null;
                        }

                        const promedio = promediosCategoriaPorTrimestre[trimestre]?.[catIndex] ?? null;
                        return (
                          <td
                            key={`prom-${trimestre}-${categoria.nombre}`}
                            colSpan={numMaterias}
                            className="text-center text-primary fw-bold"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {promedio !== null ? promedio.toFixed(2) : '-'}
                          </td>
                        );
                      })}
                      <td></td>
                    </tr>
                  </React.Fragment>
                );
              })}
              {/* Fila de promedio general */}
              <tr className="table-info">
                <td><strong>PROM. CATEGORÍA</strong></td>
                {categorias.map((categoria, catIndex) => {
                  const numMaterias = Object.keys(categoria.materias || {}).length;
                  if (numMaterias === 0) {
                    return null;
                  }

                  return (
                    <td
                      key={`prom-general-${categoria.nombre}`}
                      colSpan={numMaterias}
                      className="text-center"
                    >
                      <strong>
                        {promediosCategorias[catIndex] !== null && promediosCategorias[catIndex] !== undefined
                          ? promediosCategorias[catIndex].toFixed(2)
                          : '-'}
                      </strong>
                    </td>
                  );
                })}
                <td></td>
              </tr>
              <tr className="table-warning">
                <td><strong>PROMEDIO FINAL</strong></td>
                <td colSpan={totalColumnasMaterias} className="text-center">
                  <strong>{datosAlumno.promedios?.general || '0.0'}</strong>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr className="attendance">
                <td>Asistencias</td>
                {categorias.map((categoria, catIndex) => {
                  const numMaterias = Object.keys(categoria.materias || {}).length;
                  const trimestre = catIndex + 1;
                  return numMaterias > 0 ? (
                    <td key={catIndex} colSpan={numMaterias}>
                      <strong>{trimestre}°</strong> {datosAlumno.resumenAsistencias?.porTrimestre?.[trimestre]?.asistencias || 0}
                    </td>
                  ) : null;
                })}
                <td>TOTAL {datosAlumno.resumenAsistencias?.total?.asistencias || 0}</td>
              </tr>
              <tr className="attendance">
                <td>Inasistencias</td>
                {categorias.map((categoria, catIndex) => {
                  const numMaterias = Object.keys(categoria.materias || {}).length;
                  const trimestre = catIndex + 1;
                  return numMaterias > 0 ? (
                    <td key={catIndex} colSpan={numMaterias}>
                      <strong>{trimestre}°</strong> {datosAlumno.resumenAsistencias?.porTrimestre?.[trimestre]?.inasistencias || 0}
                    </td>
                  ) : null;
                })}
                <td>TOTAL {datosAlumno.resumenAsistencias?.total?.inasistencias || 0}</td>
              </tr>
            </tfoot>
          </table>

          <div className="mt-2">
            <h6 style={{ fontSize: '12px', marginBottom: '5px' }}>Observaciones por Trimestre</h6>
            <textarea 
              rows="4" 
              id="obsAlumno" 
              className="form-control" 
              style={{ fontSize: '10px' }}
              readOnly
              value={Object.keys(datosAlumno?.observacionesGenerales || {})
                .filter(trimestre => datosAlumno?.observacionesGenerales?.[trimestre]?.trim())
                .map(trimestre => `TRIMESTRE ${trimestre}:\n${datosAlumno?.observacionesGenerales?.[trimestre]}`)
                .join('\n\n')}
            ></textarea>
          </div>

          <div className="mt-2">
            <h6 style={{ fontSize: '12px', marginBottom: '5px' }}>Sugerencias por Trimestre</h6>
            <textarea 
              rows="4" 
              id="sugAlumno" 
              className="form-control" 
              style={{ fontSize: '10px' }}
              readOnly
              value={Object.keys(datosAlumno?.sugerenciasGenerales || {})
                .filter(trimestre => datosAlumno?.sugerenciasGenerales?.[trimestre]?.trim())
                .map(trimestre => `TRIMESTRE ${trimestre}:\n${datosAlumno?.sugerenciasGenerales?.[trimestre]}`)
                .join('\n\n')}
            ></textarea>
          </div>

          {/* Sección detallada por categorías */}
          <div className="mt-4">
            <h5 className="text-center mb-3">Detalle de Calificaciones por Categoría</h5>
            {categorias
              .filter(categoria => Object.keys(categoria.materias).length > 0) // Solo mostrar categorías con materias
              .map((categoria, catIndex) => (
              <div key={catIndex} className="mb-4">
                <h6 className="bg-primary text-white p-2 mb-3">
                  {categoria.nombre} 
                  <span className="badge bg-light text-primary ms-2">
                    {Object.keys(categoria.materias).length} materia(s)
                  </span>
                </h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm">
                    <thead className="table-light">
                      <tr>
                        <th rowSpan="2">Materia</th>
                        <th rowSpan="2">Horario</th>
                        <th colSpan="3" className="text-center">Calificaciones por Trimestre</th>
                        <th rowSpan="2">Docente(s)</th>
                      </tr>
                      <tr>
                        <th className="text-center">1°</th>
                        <th className="text-center">2°</th>
                        <th className="text-center">3°</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(categoria.materias).map((materia, matIndex) => (
                        <tr key={matIndex}>
                          <td><strong>{materia.nombre}</strong></td>
                          <td>
                            {materia.horario && materia.horario.length > 0 ? (
                              <div>
                                {materia.horario.map((horario, hIndex) => (
                                  <div key={hIndex} className="mb-1">
                                    <small>
                                      <strong>{horario.dia}</strong><br />
                                      {horario.horaInicio?.slice(0,5)} - {horario.horaFin?.slice(0,5)}<br />
                                      <span className="text-muted">Aula: {horario.aula}</span>
                                    </small>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <small className="text-muted">Sin horario</small>
                            )}
                          </td>
                          {[1, 2, 3].map(trimestre => (
                            <td key={trimestre} className="text-center">
                              {materia.calificaciones[trimestre] ? (
                                <div>
                                  <span className="badge bg-success fs-6">
                                    {materia.calificaciones[trimestre].calificacion}
                                  </span>
                                  <br />
                                  <small className="text-muted">
                                    A: {materia.calificaciones[trimestre].asistencias} | 
                                    I: {materia.calificaciones[trimestre].inasistencias}
                                  </small>
                                  {materia.calificaciones[trimestre].observaciones && (
                                    <div>
                                      <small className="text-info d-block mt-1">
                                        📝 {materia.calificaciones[trimestre].observaciones}
                                      </small>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div>
                                  <span className="text-muted">Pendiente</span>
                                  <br />
                                  <small className="text-muted">No calificado</small>
                                </div>
                              )}
                            </td>
                          ))}
                          <td>
                            {materia.docentes && materia.docentes.length > 0 ? (
                              <div>
                                {materia.docentes.map((docente, dIndex) => (
                                  <small key={dIndex} className="d-block">
                                    👨‍🏫 {docente}
                                  </small>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">Sin asignar</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-2">
            <img src={firmaCoordinadora} alt="Firma Coordinadora" style={{ maxHeight: '50px' }} /><br />
            <strong style={{ fontSize: '11px' }}>Mtra. Ana María Ramos Cortés</strong><br />
            <span style={{ fontSize: '10px' }}>Coordinadora de Primaria</span>
          </div>

          <div className="d-flex justify-content-center gap-3 mt-4">
            <button className="btn btn-danger" onClick={() => window.exportarPDF()}>Exportar PDF</button>
            <button className="btn btn-warning" onClick={() => window.exportarExcel()}>Exportar Excel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultarCalificaciones;
